import { AppModal } from '@components/form/Modal';
import { Empty, InputRef, Modal, notification } from 'antd';
import { Client, useFetchSingleRecentMessageConversationLazyQuery } from '@/types/generated/graphql';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FieldError } from '@components/field-error';
import { useEncryptedParams } from '@shared/hooks/use-encrypted-params';
import { removePlusSignFromPhoneNumber } from '@shared/utils/phone-number';
import { isValidPhoneNumber } from '@shared/utils/contact-helper';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';
import { AppAlert } from '@components/antd';
import { graphQLErrorCodeChecker } from '@shared/utils/graphql-error-code.helper';
import { RiDiscussLine } from 'react-icons/ri';
import { useModal } from '@shared/hooks/use-modal';
import { ROLE_MAPS } from '@shared/constants/roles';
import { hasRoleAccess } from '@shared/utils/roles/has-role-access.helper';
import NewBulkMessagingModal from './new-bulk-messaging.modal';
import { ContactSelectList } from './components/bulk-messaging/contact-select-list';
import { ContactItem } from './components/bulk-messaging/bulk-messaging.types';
import { useBulkMessaging } from './hooks/use-bulk-messaging';
import { useBulkMessagingRecentContacts } from './hooks/use-bulk-messaging-recent-contacts';
import { SearchContactInput } from './components/search-contact-input';
import { normalizePhoneNumber } from '@shared/utils/normalize-phone-number';
import { prefixPlus } from '@shared/utils';

type Props = {
  channelId: number;
  open: boolean;
  handleClose: VoidFunction;
};

function NewConversationModal(props: Props) {
  const { channelId = 0, open, handleClose } = props;
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchInputRef = useRef<InputRef>(null);
  const navigate = useNavigate();
  const [params, , objToEncryptedParams] = useEncryptedParams();
  const { search } = useLocation();
  const { t } = useTranslation();
  const bulkMessagingModal = useModal();
  const [canCreateBulkMessage] = hasRoleAccess(ROLE_MAPS.BULK_MESSAGING_CAMPAIGNS_CREATE);
  const { contacts, hasNextPage, loadNextRef, isSearching } = useBulkMessaging(searchInput);
  const recentContacts = useBulkMessagingRecentContacts();
  const isSearching_ = searchInput.trim().length > 0;
  const [checkRecentMessage, { loading }] = useFetchSingleRecentMessageConversationLazyQuery({
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    onError: ({ message, graphQLErrors }) => {
      const hasGraphQLErrorCode = graphQLErrorCodeChecker(graphQLErrors);
      if (hasGraphQLErrorCode('BAD_USER_INPUT')) {
        setError('blocked');
        return;
      }
      notification.error({ message });
    },
  });

  async function startConversation(phone: string) {
    const res = await checkRecentMessage({
      variables: { channelId, phoneNumber: normalizePhoneNumber(phone) },
    });
    if (res.error) return;
    handleClose();

    const clientId = res.data?.fetchSingleRecentMessageConversation?.data?.client?.id;
    openConversation(clientId, phone);
  }

  function openConversation(clientId: Client['id'], phone: string) {
    if (clientId) {
      navigate({ pathname: `/conversations/${channelId}/${clientId}`, search });
    } else {
      navigate({
        pathname: `/conversations/${channelId}/new/`,
        search: objToEncryptedParams({
          channelName: params.get('channelName') ?? '',
          phoneNumber: removePlusSignFromPhoneNumber(normalizePhoneNumber(phone) ?? ''),
        }),
      });
    }
  }

  async function checkRecentMessageHandler() {
    if (!isValidPhoneNumber(searchInput)) {
      return setError(t('phone_number_is_invalid'));
    }
    const phone = normalizePhoneNumber(prefixPlus(searchInput));
    const res = await checkRecentMessage({
      variables: {
        channelId,
        phoneNumber: normalizePhoneNumber(prefixPlus(searchInput)),
      },
    });

    if (res.error || !phone) return;
    handleClose();

    const clientId = res.data?.fetchSingleRecentMessageConversation?.data?.client?.id;
    openConversation(clientId, phone);
  }

  function handleSearchInputPressEnter() {
    if (!searchInput.trim() || !isValidPhoneNumber(searchInput)) return;
    checkRecentMessageHandler();
  }

  function handleContactClick(contact: ContactItem) {
    startConversation(contact.phoneNumber);
  }

  function resetFields() {
    setError('');
    setSearchInput('');
  }

  useEffect(resetFields, [open]);

  return (
    <Modal
      className="w-105!"
      centered
      onCancel={handleClose}
      open={open}
      afterOpenChange={(isOpen) => isOpen && searchInputRef.current?.focus()}
      title={
        <AppModal.Header>
          <AppModal.Title>{capitalize(t('new_message'))}</AppModal.Title>
          <AppModal.SubTitle>{t('start_a_new_conversation')}</AppModal.SubTitle>
        </AppModal.Header>
      }
      footer={
        <AppModal.Footer>
          <AppModal.Button
            loading={loading}
            className="w-full"
            type="primary"
            onClick={checkRecentMessageHandler}
            disabled={!searchInput.trim() || !isValidPhoneNumber(searchInput)}
          >
            {t('start_a_new_conversation')}
          </AppModal.Button>
        </AppModal.Footer>
      }
    >
      <AppModal.Body>
        {error === 'blocked' && (
          <div className="mb-2">
            <AppAlert
              showIcon
              type="error"
              message={
                <p className="text-text-600 text-sm">
                  {t('this_number_is_blocked_unblock_it_from')}
                  <span
                    className="underline cursor-pointer pl-0.5"
                    onClick={() => {
                      handleClose();
                      navigate('/settings/block-list');
                    }}
                  >
                    {t('here')}
                  </span>
                </p>
              }
            />
          </div>
        )}
        <SearchContactInput
          ref={searchInputRef}
          value={searchInput}
          onChange={(value) => {
            setError('');
            setSearchInput(value);
          }}
          onPressEnter={handleSearchInputPressEnter}
          error={error && error !== 'blocked' ? error : ''}
        />
        {error !== 'blocked' && <FieldError message={error} />}
        <div className="space-y-4 max-h-131 overflow-y-auto">
          {canCreateBulkMessage && (
            <div
              className="flex items-center gap-2.5 mt-3 px-1.5 py-2 cursor-pointer rounded-sm hover:bg-gray-100"
              onClick={bulkMessagingModal.handleOpen}
            >
              <span className="flex items-center border border-gray-500 size-9 p-2 rounded-full bg-[linear-gradient(180deg,#003399_17.31%,#011F5C_100%)]">
                <RiDiscussLine color="white" size={20} />
              </span>
              <p>{t('new_bulk_messaging')}</p>
            </div>
          )}
          {contacts.length === 0 ? (
            <div className="py-8">
              <Empty description="No contacts found" />
            </div>
          ) : (
            <>
              {!isSearching_ && (
                <ContactSelectList title="RECENT" contacts={recentContacts} onToggle={handleContactClick} />
              )}
              <ContactSelectList
                title="ALL CONTACTS"
                contacts={contacts}
                onToggle={handleContactClick}
                hasNextPage={hasNextPage}
                loadMoreRef={loadNextRef}
                isSearching={isSearching}
              />
            </>
          )}
        </div>
      </AppModal.Body>
      <NewBulkMessagingModal open={bulkMessagingModal.open} handleClose={bulkMessagingModal.handleClose} />
    </Modal>
  );
}

export default NewConversationModal;
